import React, { useState, useMemo } from 'react';
import {
  Container,
  Form,
  Row,
  Col,
  Modal,
  Badge,
  Card,
  Button,
} from 'react-bootstrap';
import './DataTable.css';

const sampleReports = [
  {
    id: 1,
    title: 'Daily Report - Executive Briefing',
    description: 'Completed all team lead meetings and daily candidate registrations.',
    type: 'daily',
    status: 'completed',
    date: '2026-09-04',
    author: 'Sunil Kumar',
    city: 'Patna',
    amount: '₹45,000',
  },
  {
    id: 2,
    title: 'Cheque Collection - Weekly Review',
    description: 'All 14 branch cheques collected, verified, and sent for clearing.',
    type: 'check',
    status: 'in-progress',
    date: '2026-09-03',
    author: 'Rajesh Verma',
    city: 'Ranchi',
    amount: '₹1,20,000',
  },
  {
    id: 3,
    title: 'Festive Campaign - Special Target',
    description: 'Planning Diwali campaign allocations and team incentives.',
    type: 'festive',
    status: 'pending',
    date: '2026-09-02',
    author: 'Anjali Gupta',
    city: 'Varanasi',
    amount: '₹85,000',
  },
  {
    id: 4,
    title: 'Promotional Offer - Tier 1 Bonus',
    description: 'New distributor bonus registrations verified and completed.',
    type: 'offer',
    status: 'completed',
    date: '2026-09-01',
    author: 'Manoj Singh',
    city: 'Lucknow',
    amount: '₹60,000',
  },
];

const DataTable = () => {
  const [reports] = useState(sampleReports);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('');

  // Sorting & Pagination
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredReports = useMemo(() => {
    let result = [...reports];

    if (searchTerm) {
      result = result.filter(
        (r) =>
          r.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.author?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      result = result.filter((r) => r.type === filterType);
    }

    if (filterStatus !== 'all') {
      result = result.filter((r) => r.status === filterStatus);
    }

    if (filterDate) {
      result = result.filter((r) => r.date === filterDate);
    }

    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [reports, searchTerm, filterType, filterStatus, filterDate, sortColumn, sortDirection]);

  const handleRowClick = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed') return <Badge bg="success">Completed</Badge>;
    if (s === 'in-progress') return <Badge bg="info" className="text-dark">In Progress</Badge>;
    return <Badge bg="warning" className="text-dark">Pending</Badge>;
  };

  const getTypeBadge = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'daily') return <Badge bg="primary">Daily</Badge>;
    if (t === 'check') return <Badge bg="success">Cheque</Badge>;
    if (t === 'festive') return <Badge bg="danger">Festive</Badge>;
    return <Badge bg="info" className="text-dark">Offer</Badge>;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredReports.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage) || 1;

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterStatus('all');
    setFilterDate('');
    setCurrentPage(1);
  };

  return (
    <Container fluid className="reporting-container">
      <div className="reporting-header-box">
        <div className="reporting-title-wrap">
          <h2 className="reporting-title">
            <span>📑</span> All Reports Explorer
          </h2>
          <p className="reporting-subtitle">Global explorer to filter, view, and organize all submitted report forms</p>
        </div>
      </div>

      {/* Filter Card */}
      <div className="filter-panel-card">
        <div className="filter-card-title">
          <span>🔍</span> Filter & Query Records
        </div>
        <div className="filter-grid">
          <div className="filter-field">
            <label className="filter-label">Search Reports</label>
            <Form.Control
              type="text"
              placeholder="Search by title, author, desc..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="filter-field">
            <label className="filter-label">Report Type</label>
            <Form.Select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Types</option>
              <option value="daily">Daily Report</option>
              <option value="check">Cheque Report</option>
              <option value="festive">Festive Report</option>
              <option value="offer">Offer Report</option>
            </Form.Select>
          </div>

          <div className="filter-field">
            <label className="filter-label">Status</label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="pending">Pending</option>
            </Form.Select>
          </div>

          <div className="filter-field">
            <label className="filter-label">Date</label>
            <Form.Control
              type="date"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="filter-actions">
            <button className="btn-filter-reset" onClick={handleClearFilters}>
              <span>↺</span> Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="table-data-card">
        {/* Table Toolbar & View Switcher */}
        <div className="table-toolbar-box">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-bold small text-muted">View Mode:</span>
            <div className="view-mode-toggle-group">
              <button
                className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                <span>📊</span> Table View
              </button>
              <button
                className={`view-mode-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                <span>🗂️</span> Card View
              </button>
            </div>
          </div>
          {viewMode === 'table' && (
            <div className="mobile-swipe-hint d-md-none">
              <span>👉</span> Swipe horizontally to view all columns
            </div>
          )}
        </div>

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="table-responsive-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                    Title {sortColumn === 'title' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                    Type {sortColumn === 'type' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th>Author</th>
                  <th>City</th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status {sortColumn === 'status' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                    Date {sortColumn === 'date' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((report) => (
                    <tr
                      key={report.id}
                      className="table-row-interactive"
                      onClick={() => handleRowClick(report)}
                    >
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar-pill">📄</div>
                          <div className="user-cell-meta">
                            <span className="user-name-bold">{report.title}</span>
                            <span className="user-sub-id text-truncate" style={{ maxWidth: '280px' }}>
                              {report.description}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>{getTypeBadge(report.type)}</td>
                      <td>{report.author || '-'}</td>
                      <td>{report.city || '-'}</td>
                      <td>{getStatusBadge(report.status)}</td>
                      <td>{report.date}</td>
                      <td>
                        <span className="badge-amount-pill">{report.amount}</span>
                      </td>
                      <td>
                        <button
                          className="btn-view-details"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(report);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8">
                      <div className="empty-table-state">
                        <div className="empty-icon">📋</div>
                        <div className="empty-title">No Reports Found</div>
                        <div className="empty-desc">Try adjusting your filters</div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-3">
            {currentItems.length > 0 ? (
              <Row className="g-3">
                {currentItems.map((report) => (
                  <Col key={report.id} xs={12} md={6} xl={4}>
                    <Card
                      className="h-100 shadow-sm border"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(report)}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="fw-bold mb-0 text-primary">{report.title}</h6>
                            <small className="text-muted">{report.date} • {report.author}</small>
                          </div>
                          {getTypeBadge(report.type)}
                        </div>
                        <p className="small text-secondary mb-2">{report.description}</p>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>{getStatusBadge(report.status)}</div>
                          <span className="badge-amount-pill">{report.amount}</span>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="empty-table-state">
                <div className="empty-icon">📋</div>
                <div className="empty-title">No Reports Found</div>
                <div className="empty-desc">Try adjusting your filters</div>
              </div>
            )}
          </div>
        )}

        {/* Pagination Bar */}
        <div className="pagination-container-box">
          <span className="pagination-summary-text">
            Showing {filteredReports.length > 0 ? indexOfFirstItem + 1 : 0} to{' '}
            {Math.min(indexOfLastItem, filteredReports.length)} of {filteredReports.length} records
          </span>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
            >
              «
            </button>
            <button
              className="pagination-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              ‹
            </button>
            <span className="px-2 fw-bold text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="pagination-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              ›
            </button>
            <button
              className="pagination-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              »
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        className="detail-modal-custom"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span>📑</span> Report Overview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <div className="modal-details-grid">
              <div className="detail-field-box">
                <span className="detail-field-label">Title</span>
                <span className="detail-field-val">{selectedReport.title}</span>
              </div>
              <div className="detail-field-box">
                <span className="detail-field-label">Type</span>
                <span className="detail-field-val">{selectedReport.type}</span>
              </div>
              <div className="detail-field-box">
                <span className="detail-field-label">Status</span>
                <span className="detail-field-val">{selectedReport.status}</span>
              </div>
              <div className="detail-field-box">
                <span className="detail-field-label">Date</span>
                <span className="detail-field-val">{selectedReport.date}</span>
              </div>
              <div className="detail-field-box">
                <span className="detail-field-label">Author</span>
                <span className="detail-field-val">{selectedReport.author}</span>
              </div>
              <div className="detail-field-box">
                <span className="detail-field-label">Amount / Value</span>
                <span className="detail-field-val">{selectedReport.amount}</span>
              </div>
              <div className="detail-field-box" style={{ gridColumn: '1 / -1' }}>
                <span className="detail-field-label">Description</span>
                <span className="detail-field-val">{selectedReport.description}</span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DataTable;
